import { useMutation } from "@tanstack/react-query"
import { AlertTriangle, XCircle } from "lucide-react"
import { useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import {
  ProveedorDeLaFactura,
  type ProveedorElegido,
} from "@/componentes/ordenes/ProveedorDeLaFactura"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

/**
 * Lo que se leyó de una factura, con sus dudas resueltas AQUÍ MISMO.
 *
 * ⚠ **Una duda se contesta con una selección, no leyendo otra vez.** La moneda que el
 * papel no dice, si el total lleva IVA dentro, qué es cada renglón, quién es el
 * proveedor: leer de nuevo daría lo mismo, porque el papel dice lo mismo. Cada cambio
 * vuelve a VERIFICARSE en el servidor —`/facturas-ia/verificar`, sin llamar a Gemini—
 * y lo calculado, los bloqueos y los avisos se ven al instante.
 *
 * ⚠ **Lo que sí se corrige leyendo otra vez es una CIFRA mal leída**: eso no se
 * selecciona, se le dice al lector en el contexto y se relee. Los ajustes no tocan ni
 * renglones ni total: son lo que el papel no dice.
 *
 * ⚠ **Lo decidido queda escrito** en la orden («Decidido al revisar: moneda BS»), aparte
 * de las dudas del lector. Ver `services/ajustes_factura.py`.
 */

export type Naturaleza = "SERVICIO" | "MATERIAL" | "CONSUMIBLE" | "BIEN"
export type Moneda = "BS" | "USD" | "EUR" | "USDT"

type Linea = {
  descripcion: string
  cantidad: string
  precio_unitario: string | null
  subtotal: string | null
  marca_iva: "GRAVADO" | "EXENTO" | "NO_INDICA"
  naturaleza_sugerida: Naturaleza
  motivo_naturaleza: string
}

export type Lectura = {
  lectura_id: string
  factura: {
    tipo_documento: string
    proveedor_nombre: string | null
    proveedor_rif: string | null
    proveedor_extranjero: boolean
    proveedor_identificador: string | null
    numero_documento: string | null
    numero_control: string | null
    fecha_emision: string | null
    moneda: Moneda | null
    lineas: Linea[]
    iva: string | null
    otros_impuestos: { concepto: string; monto: string | null }[]
    total: string | null
    total_literal: string | null
    concepto_sugerido: string
    confianza: "ALTA" | "MEDIA" | "BAJA"
    modelo_usado: string
    decisiones?: string[]
    /** Una solicitud de fondos sin factura: la orden nace fuera del libro de compras. */
    sin_documento_fiscal?: boolean
    /** Cómo pagarle según el mensaje. Va a las notas, nunca a la ficha del proveedor. */
    datos_pago?: string | null
  }
  veredicto: {
    se_puede_crear: boolean
    bloqueos: string[]
    avisos: string[]
    exento_calculado: string
    base_calculada: string
    iva_calculado: string
    otros_calculado: string
    total_calculado: string
  }
  proveedor: { id: number; nombre: string } | null
}

/** Lo que se manda al servidor: sólo lo que alguien decidió. */
export type Ajustes = {
  moneda?: Moneda
  lleva_iva?: boolean
  lineas: { indice: number; naturaleza?: Naturaleza; exento?: boolean }[]
  /** (23/10) El total impreso no es el de la orden: se usa el de los renglones. */
  usar_total_calculado?: boolean
}

type AjusteLinea = { naturaleza?: Naturaleza; exento?: boolean }

const NATURALEZAS: { valor: Naturaleza; etiqueta: string }[] = [
  { valor: "SERVICIO", etiqueta: "Servicio" },
  { valor: "MATERIAL", etiqueta: "Uso inmediato" },
  { valor: "CONSUMIBLE", etiqueta: "Consumible" },
  { valor: "BIEN", etiqueta: "Bien" },
]
const MONEDAS: Moneda[] = ["BS", "USD", "EUR", "USDT"]

const CONFIANZA: Record<Lectura["factura"]["confianza"], Tono> = {
  ALTA: "exito",
  MEDIA: "aviso",
  BAJA: "peligro",
}

export function ResultadoLectura({
  lectura,
  creando,
  errorCrear,
  onCrear,
  onReleer,
}: {
  lectura: Lectura
  creando: boolean
  errorCrear: string | null
  onCrear: (ajustes: Ajustes, proveedorId: number | null) => void
  onReleer: () => void
}) {
  const [moneda, setMoneda] = useState<Moneda | null>(null)
  const [llevaIva, setLlevaIva] = useState<boolean | null>(null)
  const [totalCalculado, setTotalCalculado] = useState(false)
  const [porLinea, setPorLinea] = useState<Record<number, AjusteLinea>>({})
  const [elegido, setElegido] = useState<ProveedorElegido | null>(null)

  const ajustes: Ajustes = {
    ...(moneda !== null ? { moneda } : {}),
    ...(llevaIva !== null ? { lleva_iva: llevaIva } : {}),
    lineas: Object.entries(porLinea).map(([i, a]) => ({ indice: Number(i), ...a })),
    ...(totalCalculado ? { usar_total_calculado: true } : {}),
  }
  const hayAjustes =
    moneda !== null || llevaIva !== null || ajustes.lineas.length > 0 || totalCalculado
  const clave = JSON.stringify(ajustes)

  const verificar = useMutation({
    mutationFn: (cuerpo: Ajustes) =>
      api.post<Lectura>("/api/v1/facturas-ia/verificar", {
        lectura_id: lectura.lectura_id,
        ajustes: cuerpo,
      }),
  })
  const { mutate: reverificar, reset: olvidarVerificacion } = verificar

  // Se reverifica al dejar de tocar: un clic en cada renglón no es una petición cada uno.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `clave` resume los ajustes
  useEffect(() => {
    if (!hayAjustes) {
      olvidarVerificacion()
      return
    }
    const espera = window.setTimeout(() => reverificar(JSON.parse(clave) as Ajustes), 300)
    return () => window.clearTimeout(espera)
  }, [clave])

  const vista = hayAjustes && verificar.data ? verificar.data : lectura
  const { factura, veredicto } = vista
  const proveedor = elegido ?? vista.proveedor
  const simbolo = factura.moneda ?? ""
  const verificando = verificar.isPending
  const creable = veredicto.se_puede_crear && proveedor !== null && !verificando

  function ajustarLinea(indice: number, cambio: AjusteLinea) {
    setPorLinea((actual) => ({ ...actual, [indice]: { ...actual[indice], ...cambio } }))
  }

  return (
    <section className="tarjeta space-y-3 p-3 sm:p-4" aria-label="Lo que se leyó">
      <div className="flex flex-wrap items-center gap-2">
        <Insignia tono={CONFIANZA[factura.confianza]}>Confianza {factura.confianza}</Insignia>
        {proveedor ? (
          <Insignia tono="exito">Proveedor: {proveedor.nombre}</Insignia>
        ) : (
          <Insignia tono="peligro">Proveedor no está en el catálogo</Insignia>
        )}
        {factura.proveedor_extranjero && <Insignia tono="info">Extranjero</Insignia>}
        <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Leída por {factura.modelo_usado}
          {verificando && " · verificando…"}
        </span>
      </div>

      <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        <Dato
          etiqueta="Proveedor"
          valor={`${factura.proveedor_nombre ?? "?"} · ${
            factura.proveedor_extranjero
              ? (factura.proveedor_identificador ?? "sin identificador")
              : (factura.proveedor_rif ?? "sin RIF")
          }`}
        />
        {factura.sin_documento_fiscal ? (
          <>
            <Dato etiqueta="Documento" valor="Solicitud de fondos, sin factura" />
            <Dato etiqueta="Mensaje del" valor={comoFecha(factura.fecha_emision) || "sin fecha"} />
            <Dato etiqueta="Datos de pago" valor={factura.datos_pago ?? "—"} ancho />
          </>
        ) : (
          <>
            <Dato etiqueta="Documento" valor={factura.numero_documento ?? "—"} />
            <Dato etiqueta="Control" valor={factura.numero_control ?? "—"} />
            <Dato etiqueta="Emisión" valor={comoFecha(factura.fecha_emision) || "sin fecha"} />
          </>
        )}
        <Dato etiqueta="Descripción" valor={factura.concepto_sugerido} ancho />
      </dl>

      {/* ⚠ Las dudas que el papel no contesta, contestadas aquí. La moneda se pide en
          rojo cuando no se leyó: sin ella no hay orden. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span style={{ color: lectura.factura.moneda ? undefined : "var(--destructive)" }}>
            {lectura.factura.moneda ? "Moneda" : "¿En qué moneda está? (no se leyó)"}
          </span>
          <select
            className="campo mt-1 w-full"
            value={moneda ?? lectura.factura.moneda ?? ""}
            onChange={(e) => setMoneda(e.target.value === "" ? null : (e.target.value as Moneda))}
          >
            {!lectura.factura.moneda && <option value="">Elige la moneda…</option>}
            {MONEDAS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          IVA
          <select
            className="campo mt-1 w-full"
            value={llevaIva === null ? "" : llevaIva ? "si" : "no"}
            onChange={(e) => setLlevaIva(e.target.value === "" ? null : e.target.value === "si")}
          >
            <option value="">Según el papel</option>
            <option value="si">Lleva IVA 16 % dentro del total</option>
            <option value="no">No lleva IVA</option>
          </select>
        </label>
      </div>

      <Tabla etiqueta="Renglones leídos de la factura">
        <Cabecera>
          <Th>Renglón</Th>
          <Th>Qué es</Th>
          <Th>Exento</Th>
          <Th derecha>Cant.</Th>
          <Th derecha>Importe</Th>
        </Cabecera>
        <Cuerpo>
          {factura.lineas.map((linea, i) => (
            // Sin identificador propio: el orden del papel ES la identidad del renglón.
            // biome-ignore lint/suspicious/noArrayIndexKey: la lista no se reordena
            <Fila key={i}>
              <Td>{linea.descripcion}</Td>
              <Td>
                <select
                  aria-label={`Qué es «${linea.descripcion}»`}
                  title={linea.motivo_naturaleza}
                  className="campo py-1"
                  value={linea.naturaleza_sugerida}
                  onChange={(e) => ajustarLinea(i, { naturaleza: e.target.value as Naturaleza })}
                >
                  {NATURALEZAS.map((n) => (
                    <option key={n.valor} value={n.valor}>
                      {n.etiqueta}
                    </option>
                  ))}
                </select>
              </Td>
              <Td>
                <input
                  type="checkbox"
                  aria-label={`«${linea.descripcion}» exento de IVA`}
                  checked={linea.marca_iva === "EXENTO"}
                  onChange={(e) => ajustarLinea(i, { exento: e.target.checked })}
                />
              </Td>
              <Td derecha numero>
                {linea.cantidad}
              </Td>
              <Td derecha numero>
                {linea.subtotal ?? "?"}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>

      {/* ⚠ Lo impreso y lo calculado, uno al lado del otro: es la verificación a la
          vista. Si no coinciden, el bloqueo de abajo dice por qué. */}
      <dl className="space-y-1 text-sm">
        {Number(veredicto.exento_calculado) > 0 && (
          <Cifra etiqueta="Exento" valor={veredicto.exento_calculado} moneda={simbolo} />
        )}
        <Cifra etiqueta="Base" valor={veredicto.base_calculada} moneda={simbolo} />
        <Cifra etiqueta="IVA" valor={veredicto.iva_calculado} moneda={simbolo} />
        {/* Cada impuesto que NO es IVA venezolano, con su rótulo impreso. */}
        {factura.otros_impuestos.map((otro) => (
          <Cifra
            key={otro.concepto}
            etiqueta={`Otro impuesto: ${otro.concepto}`}
            valor={otro.monto ?? "?"}
            moneda={simbolo}
          />
        ))}
        <Cifra
          etiqueta="Total calculado"
          valor={veredicto.total_calculado}
          moneda={simbolo}
          fuerte
        />
        <Cifra
          etiqueta="Total impreso"
          valor={factura.total ?? "?"}
          moneda={simbolo}
          detalle={factura.total_literal ?? undefined}
        />
      </dl>

      {veredicto.bloqueos.length > 0 && (
        <Lista titulo="No se puede crear la orden" items={veredicto.bloqueos} peligro />
      )}
      {/* (23/10) Un total impreso que no cuadra —una nota en dólares con el «TOTAL Bs.» en
          bolívares— no hace perder la lectura: se puede usar el de los renglones, y la
          decisión queda anotada en la orden. */}
      {(totalCalculado ||
        veredicto.bloqueos.some((b) => b.startsWith("Los renglones no cuadran"))) && (
        <label className="tarjeta flex items-start gap-2 p-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={totalCalculado}
            onChange={(e) => setTotalCalculado(e.target.checked)}
          />
          <span>
            <span className="font-medium">Usar el total calculado de los renglones</span>
            <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
              El total impreso no es el de la orden (por ejemplo, viene en otra moneda). La orden
              lleva la suma de los renglones con su IVA, y queda anotado qué decía el papel.
            </span>
          </span>
        </label>
      )}
      {vista.proveedor === null && (
        <ProveedorDeLaFactura leido={factura} elegido={elegido} onElegir={setElegido} />
      )}
      {veredicto.avisos.length > 0 && <Lista titulo="Revisa" items={veredicto.avisos} />}
      {verificar.error instanceof ErrorApi && (
        <p className="text-sm" role="alert" style={{ color: "var(--destructive)" }}>
          {verificar.error.message}
        </p>
      )}
      {errorCrear && (
        <p className="text-sm" role="alert" style={{ color: "var(--destructive)" }}>
          {errorCrear}
        </p>
      )}

      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        ¿Leyó mal una cifra o un nombre? Díselo en el contexto de arriba —«el total es 77.952», «son
        4 tablas»— y pulsa «Leer otra vez».
      </p>
      <div className="flex flex-wrap justify-end gap-2">
        <Boton onClick={onReleer}>Leer otra vez</Boton>
        <Boton
          variante="primario"
          disabled={!creable || creando}
          onClick={() => onCrear(ajustes, elegido?.id ?? null)}
          title={creable ? undefined : "Resuelve lo que se indica arriba"}
        >
          {creando ? "Creando…" : "Crear orden en borrador"}
        </Boton>
      </div>
    </section>
  )
}

function Dato({ etiqueta, valor, ancho }: { etiqueta: string; valor: string; ancho?: boolean }) {
  return (
    <div className={ancho ? "sm:col-span-2" : undefined}>
      <dt className="inline" style={{ color: "var(--fg-muted)" }}>
        {etiqueta}:{" "}
      </dt>
      <dd className="inline tabular-nums">{valor}</dd>
    </div>
  )
}

function Cifra({
  etiqueta,
  valor,
  moneda,
  fuerte,
  detalle,
}: {
  etiqueta: string
  valor: string
  moneda: string
  fuerte?: boolean
  detalle?: string
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt style={{ color: "var(--fg-muted)" }}>
        {etiqueta}
        {detalle && <span className="ml-1 text-xs">(«{detalle}»)</span>}
      </dt>
      <dd className={fuerte ? "font-semibold tabular-nums" : "tabular-nums"}>
        {valor} {moneda}
      </dd>
    </div>
  )
}

export function Lista({
  titulo,
  items,
  peligro,
}: {
  titulo: string
  items: string[]
  peligro?: boolean
}) {
  const color = peligro ? "var(--destructive)" : "var(--warning)"
  const Icono = peligro ? XCircle : AlertTriangle
  return (
    <div className="space-y-1 text-sm" role={peligro ? "alert" : undefined}>
      <p className="flex items-center gap-2 font-medium" style={{ color }}>
        <Icono size={16} aria-hidden={true} /> {titulo}
      </p>
      <ul className="list-disc space-y-0.5 pl-6">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  )
}
